import { useEffect, useState } from 'react'
import { Fuel, RefreshCw, Settings, Plus, Trash2, X } from 'lucide-react'
import api from '../services/api'

interface Tanque {
  item: number; nome: string; capacidade: number; saldo: number; percentual: number; custoMedio: number; valor: number
  dataSaldo: string | null; mediaSaidaDia: number; diasAutonomia: number | null; vermelho: number; amarelo: number
}
interface Config { item: string; nome: string; capacidade: string; vermelho: string; amarelo: string; descricao?: string | null }

const fmtN = (v: number, c: number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const br = (d: string | null) => (d ? d.split('-').reverse().join('/') : '—')
// vermelho/amarelo = % do tanque até onde cada cor vale (configurável por tanque); acima do amarelo é verde.
export const corNivel = (p: number, vermelho = 20, amarelo = 40) => (p < vermelho ? '#dc2626' : p < amarelo ? '#f59e0b' : '#16a34a')

// Medidor de painel (E → F) com ponteiro animado.
export function Medidor({ pct, largura = 230, vermelho = 20, amarelo = 40 }: { pct: number; largura?: number; vermelho?: number; amarelo?: number }) {
  const [ang, setAng] = useState(-90)
  useEffect(() => { const t = setTimeout(() => setAng(-90 + 180 * Math.min(Math.max(pct, 0), 100) / 100), 120); return () => clearTimeout(t) }, [pct])
  const arco = (de: number, ate: number, r: number) => {
    const p = (a: number) => { const rad = (a - 180) * Math.PI / 180; return [100 + r * Math.cos(rad), 100 + r * Math.sin(rad)] }
    const [x1, y1] = p(de), [x2, y2] = p(ate)
    return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`
  }
  const marcas = [0, 25, 50, 75, 100]
  return (
    <svg viewBox="0 0 200 118" style={{ width: '100%', maxWidth: largura }}>
      <path d={arco(0, vermelho * 1.8, 78)} stroke="#dc2626" strokeWidth="12" fill="none" />
      <path d={arco(vermelho * 1.8, amarelo * 1.8, 78)} stroke="#f59e0b" strokeWidth="12" fill="none" />
      <path d={arco(amarelo * 1.8, 180, 78)} stroke="#16a34a" strokeWidth="12" fill="none" />
      {marcas.map(m => {
        const rad = (m * 1.8 - 180) * Math.PI / 180
        return <line key={m} x1={100 + 62 * Math.cos(rad)} y1={100 + 62 * Math.sin(rad)} x2={100 + 70 * Math.cos(rad)} y2={100 + 70 * Math.sin(rad)} stroke="#334155" strokeWidth="2.5" />
      })}
      <text x="22" y="114" fontSize="13" fontWeight="800" fill="#dc2626">E</text>
      <text x="100" y="40" fontSize="10" fontWeight="700" fill="#475569" textAnchor="middle">½</text>
      <text x="170" y="114" fontSize="13" fontWeight="800" fill="#16a34a">F</text>
      <g style={{ transform: `rotate(${ang}deg)`, transformOrigin: '100px 100px', transition: 'transform 1.8s cubic-bezier(.34,1.4,.64,1)' }}>
        <line x1="100" y1="100" x2="100" y2="34" stroke="#0f172a" strokeWidth="3.5" strokeLinecap="round" />
      </g>
      <circle cx="100" cy="100" r="8" fill="#0f172a" />
      <circle cx="100" cy="100" r="3" fill="#e2e8f0" />
    </svg>
  )
}

// Tanque com o líquido subindo e onda na superfície.
export function TanqueVisual({ pct, cor, id, largura = 80 }: { pct: number; cor: string; id: string; largura?: number }) {
  const [nivel, setNivel] = useState(0)
  useEffect(() => { const t = setTimeout(() => setNivel(Math.min(Math.max(pct, 0), 100)), 120); return () => clearTimeout(t) }, [pct])
  const alturaUtil = 150, topo = 10 + alturaUtil * (1 - nivel / 100)
  return (
    <svg viewBox="0 0 90 170" style={{ width: largura }}>
      <defs>
        <clipPath id={`clip-${id}`}><rect x="10" y="10" width="70" height="150" rx="14" /></clipPath>
      </defs>
      <rect x="10" y="10" width="70" height="150" rx="14" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="2" />
      <g clipPath={`url(#clip-${id})`}>
        <g style={{ transform: `translateY(${topo}px)`, transition: 'transform 1.8s ease-out' }}>
          <path className="onda" d="M -70 6 Q -52 0 -35 6 T 0 6 T 35 6 T 70 6 T 105 6 T 140 6 V 200 H -70 Z" fill={cor} opacity="0.85" />
        </g>
      </g>
      {[0.25, 0.5, 0.75].map(f => <line key={f} x1="10" x2="20" y1={10 + 150 * f} y2={10 + 150 * f} stroke="#94a3b8" strokeWidth="1.5" />)}
      <rect x="35" y="2" width="20" height="10" rx="3" fill="#94a3b8" />
    </svg>
  )
}

// Aba Saldos do Combustível: saldo atual de cada tanque (estoque do almoxarifado) com medidor animado.
export default function SaldoTanques() {
  const [tanques, setTanques] = useState<Tanque[] | null>(null)
  const [erro, setErro] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [config, setConfig] = useState<Config[] | null>(null)
  const [erroCfg, setErroCfg] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar() {
    setErro(''); setCarregando(true)
    try { setTanques((await api.get('/combustivel/saldos-tanques')).data) }
    catch (e: any) { setErro(e?.response?.data?.error || 'Erro ao ler saldos.') }
    finally { setCarregando(false) }
  }
  useEffect(() => { carregar(); const t = setInterval(carregar, 60000); return () => clearInterval(t) }, [])

  async function abrirConfig() {
    setErroCfg('')
    const r = (await api.get('/combustivel/saldos-tanques/config')).data as { item: number; nome: string; capacidade: number; vermelho: number; amarelo: number }[]
    const lista: Config[] = r.map(t => ({ item: String(t.item), nome: t.nome, capacidade: String(t.capacidade), vermelho: String(t.vermelho), amarelo: String(t.amarelo) }))
    for (const c of lista) c.descricao = (await api.get(`/combustivel/saldos-tanques/item/${c.item}`)).data.descricao
    setConfig(lista)
  }
  async function buscarDescricao(i: number) {
    const cod = config?.[i]?.item
    if (!cod) return
    const d = (await api.get(`/combustivel/saldos-tanques/item/${cod}`)).data.descricao
    setConfig(c => c && c.map((x, k) => (k === i ? { ...x, descricao: d } : x)))
  }
  async function salvarConfig() {
    setErroCfg(''); setSalvando(true)
    try {
      await api.put('/combustivel/saldos-tanques/config', { tanques: config!.map(c => ({ item: Number(c.item), nome: c.nome, capacidade: Number(c.capacidade.replace(',', '.')), vermelho: Number(c.vermelho.replace(',', '.')), amarelo: Number(c.amarelo.replace(',', '.')) })) })
      setConfig(null); await carregar()
    } catch (e: any) { setErroCfg(e?.response?.data?.error || 'Erro ao salvar.') }
    finally { setSalvando(false) }
  }

  const info = (rotulo: string, valor: string, cor = '#0f172a') => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.78rem', padding: '3px 0', borderBottom: '1px dashed #e2e8f0' }}>
      <span style={{ color: '#64748b' }}>{rotulo}</span><b style={{ color: cor, fontVariantNumeric: 'tabular-nums' }}>{valor}</b>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <style>{`@keyframes ondaCombustivel { from { transform: translateX(0) } to { transform: translateX(70px) } } .onda { animation: ondaCombustivel 2.4s linear infinite }`}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 9, background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <Fuel size={19} color="#fff" />
        </span>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem', color: '#1b3f7b' }}>Saldo dos Tanques</h2>
          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Saldo atual no almoxarifado • atualiza sozinho a cada minuto</div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={abrirConfig} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Settings size={14} /> Configurar tanques</button>
        <button className="btn btn-primary btn-sm" onClick={carregar} disabled={carregando} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><RefreshCw size={14} /> {carregando ? 'Atualizando...' : 'Atualizar'}</button>
      </div>
      {erro && <div style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b' }}>{erro}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
        {tanques?.map(t => {
          const cor = corNivel(t.percentual, t.vermelho, t.amarelo)
          return (
            <div key={t.item} className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)', borderTop: `4px solid ${cor}` }}>
              <div className="modal-corpo">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div style={{ fontWeight: 800, color: '#1b3f7b', fontSize: '1rem' }}>{t.nome}</div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>item {t.item}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.3rem' }}>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <Medidor pct={t.percentual} vermelho={t.vermelho} amarelo={t.amarelo} />
                    <div style={{ fontSize: '1.6rem', fontWeight: 900, color: cor, lineHeight: 1 }}>{fmtN(t.percentual, 1)}%</div>
                    <div style={{ fontSize: '0.8rem', color: '#334155', marginTop: 3 }}><b>{fmtN(t.saldo, 0)} L</b> de {fmtN(t.capacidade, 0)} L</div>
                  </div>
                  <TanqueVisual pct={t.percentual} cor={cor} id={String(t.item)} />
                </div>
                {t.percentual < t.vermelho && <div style={{ marginTop: 6, fontSize: '0.75rem', fontWeight: 700, color: '#dc2626' }}>⚠ Nível baixo — programar compra</div>}
                <div style={{ marginTop: '0.6rem' }}>
                  {info('Espaço livre', `${fmtN(Math.max(t.capacidade - t.saldo, 0), 0)} L`)}
                  {info('Valor em estoque', `R$ ${fmtN(t.valor, 2)}`)}
                  {info('Custo médio', `R$ ${fmtN(t.custoMedio, 4)} /L`)}
                  {info('Saída média (30 dias)', `${fmtN(t.mediaSaidaDia, 1)} L/dia`)}
                  {info('Autonomia', t.diasAutonomia == null ? '—' : `${fmtN(t.diasAutonomia, 1)} dia(s)`, t.diasAutonomia != null && t.diasAutonomia < 3 ? '#dc2626' : '#0f172a')}
                  {info('Saldo de', br(t.dataSaldo))}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {config && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div className="modal-box" style={{ width: 'min(900px, 95vw)', maxWidth: 'none', maxHeight: '90vh' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Configurar tanques</h2>
              <button onClick={() => setConfig(null)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div className="modal-corpo">
              <div style={{ fontSize: '0.76rem', color: '#64748b', marginBottom: '0.6rem' }}>Item = código do combustível no cadastro de itens (estoque). Capacidade em litros. Cores do nível: vermelho até o 1º percentual, amarelo até o 2º e verde acima.</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead><tr style={{ textAlign: 'left', color: '#475569' }}><th>Item</th><th>Descrição no cadastro</th><th>Nome no painel</th><th>Capacidade (L)</th><th title="Até quantos % do tanque o nível fica vermelho">Vermelho até (%)</th><th title="Até quantos % do tanque o nível fica amarelo; acima disso fica verde">Amarelo até (%)</th><th /></tr></thead>
                <tbody>
                  {config.map((c, i) => (
                    <tr key={i}>
                      <td style={{ padding: 3 }}><input value={c.item} maxLength={9} style={{ width: 80 }} onChange={e => setConfig(config.map((x, k) => (k === i ? { ...x, item: e.target.value.replace(/\D/g, ''), descricao: undefined } : x)))} onBlur={() => buscarDescricao(i)} /></td>
                      <td style={{ padding: 3, color: c.descricao === null ? '#dc2626' : '#475569', fontSize: '0.74rem' }}>{c.descricao === null ? 'item não encontrado' : c.descricao ?? ''}</td>
                      <td style={{ padding: 3 }}><input value={c.nome} maxLength={40} style={{ width: 150 }} onChange={e => setConfig(config.map((x, k) => (k === i ? { ...x, nome: e.target.value } : x)))} /></td>
                      <td style={{ padding: 3 }}><input value={c.capacidade} maxLength={9} style={{ width: 90, textAlign: 'right' }} onChange={e => setConfig(config.map((x, k) => (k === i ? { ...x, capacidade: e.target.value.replace(/[^\d,]/g, '') } : x)))} /></td>
                      <td style={{ padding: 3 }}><input value={c.vermelho} maxLength={5} style={{ width: 60, textAlign: 'right' }} onChange={e => setConfig(config.map((x, k) => (k === i ? { ...x, vermelho: e.target.value.replace(/[^\d,]/g, '') } : x)))} /></td>
                      <td style={{ padding: 3 }}><input value={c.amarelo} maxLength={5} style={{ width: 60, textAlign: 'right' }} onChange={e => setConfig(config.map((x, k) => (k === i ? { ...x, amarelo: e.target.value.replace(/[^\d,]/g, '') } : x)))} /></td>
                      <td style={{ padding: 3 }}><button title="Remover" onClick={() => setConfig(config.filter((_, k) => k !== i))} style={{ border: 'none', background: '#b91c1c', color: '#fff', borderRadius: 5, padding: '2px 6px', cursor: 'pointer' }}><Trash2 size={13} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => setConfig([...config, { item: '', nome: '', capacidade: '', vermelho: '20', amarelo: '40' }])}><Plus size={13} /> Adicionar tanque</button>
              {erroCfg && <div style={{ marginTop: '0.7rem', fontSize: '0.8rem', fontWeight: 600, color: '#991b1b' }}>{erroCfg}</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={salvarConfig} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
              <button className="btn btn-secondary" onClick={() => setConfig(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
